import { useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import { useToast } from "@chakra-ui/react";
import { Mail, Heart, QrCode, MessageCircle, Globe, Shirt } from "lucide-react";
import { getLovewallSubscriptionPlans } from "../../apis/subscriptions/get.lovewall.subscription.plans";
import { buyLovewallSubscriptionPlan } from "../../apis/subscriptions/buy.lovewall.subscription.plan";
import { verifyTransaction } from "../../apis/transactions/verify.transaction";
import { getAllInAppPackages } from "../../apis/mail-credits/get.all.in.app.packages";
import { buyInAppPackage } from "../../apis/mail-credits/buy.in.app.package";
import {
  LovewallSubscriptionPlan,
  LovewallSubscriptionPlanKind,
} from "../../types/subscription";
import { InAppPackage } from "../../types/mailcredit";
import { activePaymentClient, activePaymentConfig } from "../../utils/payment";
import { editorState } from "../../utils/editor";
import { subscriptionStore } from "../../utils/subscription";
import { mailCreditsStore, userStore } from "../../hooks/store";

type AddOnType = "subscription" | "package";

type AddOn = {
  id: number;
  title: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any;
  type: AddOnType;
  planKind?: LovewallSubscriptionPlanKind;
  packageName?: string;
};

const addOnsConfig: AddOn[] = [
  {
    id: 1,
    title: "Essentials Pack",
    icon: Mail,
    type: "package",
    packageName: "essentials",
  },
  {
    id: 2,
    title: "Premium Pack",
    icon: Heart,
    type: "package",
    packageName: "premium",
  },
  {
    id: 3,
    title: "Deluxe Pack",
    icon: QrCode,
    type: "package",
    packageName: "deluxe",
  },
  {
    id: 4,
    title: "Lite",
    icon: MessageCircle,
    type: "subscription",
    planKind: LovewallSubscriptionPlanKind.Lite,
  },
  {
    id: 5,
    title: "Standard",
    icon: Globe,
    type: "subscription",
    planKind: LovewallSubscriptionPlanKind.Standard,
  },
  {
    id: 6,
    title: "Premium",
    icon: Shirt,
    type: "subscription",
    planKind: LovewallSubscriptionPlanKind.Premium,
  },
];

function CanTransitionTo(
  currentPlan: LovewallSubscriptionPlanKind,
  targetPlan: LovewallSubscriptionPlanKind
) {
  const validTransitions = {
    Free: [
      LovewallSubscriptionPlanKind.Lite,
      LovewallSubscriptionPlanKind.Standard,
      LovewallSubscriptionPlanKind.Premium,
    ],
    Lite: [
      LovewallSubscriptionPlanKind.Lite,
      LovewallSubscriptionPlanKind.Standard,
      LovewallSubscriptionPlanKind.Premium,
    ],
    Standard: [
      LovewallSubscriptionPlanKind.Standard,
      LovewallSubscriptionPlanKind.Premium,
    ],
    Premium: [LovewallSubscriptionPlanKind.Premium],
  };

  const isValid = validTransitions[currentPlan]?.includes(targetPlan);
  return {
    isValid,
    message: isValid
      ? ""
      : `Cannot transition from ${currentPlan} to ${targetPlan}`,
  };
}

const AddOnsSection = () => {
  const { XAccountHash } = useSnapshot(editorState);
  const { data: lwSubscription } = useSnapshot(subscriptionStore);
  const { inAppPackages } = useSnapshot(mailCreditsStore);
  const {
    data: { email },
  } = useSnapshot(userStore);

  const [subscriptionPlans, setSubscriptionPlans] = useState<
    LovewallSubscriptionPlan[]
  >([]);
  const [loadingId, setLoadingId] = useState<number | null>(null);

  const toast = useToast({
    position: "top",
    containerStyle: { zIndex: 9 },
  });

  const initializePayment = activePaymentConfig.usePaymentHook({
    ...activePaymentConfig,
    email,
  });

  useEffect(() => {
    getLovewallSubscriptionPlans({
      headers: {
        "X-Account-Hash": XAccountHash || "",
      },
      onCompleted(params) {
        setSubscriptionPlans(params.data);
      },
      onError(err) {
        console.error(err);
      },
    });

    getAllInAppPackages({
      headers: {
        "X-Account-Hash": XAccountHash || "",
      },
      onCompleted: (data) => {
        mailCreditsStore.inAppPackages = data.data;
      },
      onError: (err) => {
        console.error(err);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubscriptionPurchase = (
    addOn: AddOn,
    subscriptionPlan: LovewallSubscriptionPlan
  ) => {
    setLoadingId(addOn.id);

    const currentPlan = subscriptionPlans.find(
      (plan) => plan._id === lwSubscription?.lwsPlanID
    );
    const currentPlanName =
      currentPlan?.name || LovewallSubscriptionPlanKind.Free;
    const canTransition = CanTransitionTo(
      currentPlanName,
      subscriptionPlan.name
    );

    if (!canTransition.isValid) {
      toast({
        status: "error",
        description: canTransition.message || `Transition not allowed`,
      });
      setLoadingId(null);
      return;
    }

    buyLovewallSubscriptionPlan({
      headers: {
        "X-Account-Hash": XAccountHash,
      },
      body: {
        lwspID: subscriptionPlan._id,
        paymentClient: activePaymentClient,
      },
      onCompleted(params) {
        initializePayment({
          config: {
            email,
            reference: params.data.transactionReference,
            amount: params.data.amount * 100,
          },
          onSuccess: () => {
            setLoadingId(null);
            toast({
              status: "loading",
              description: `${subscriptionPlan.name} plan purchase processing`,
            });
            verifyTransaction({
              headers: {
                "X-Account-Hash": XAccountHash || "",
              },
              body: {
                transactionReference: params.data.transactionReference,
                paymentClient: activePaymentClient,
              },
              onCompleted() {
                toast({
                  status: "success",
                  description: `${subscriptionPlan.name} plan activated`,
                });
              },
              onError(err) {
                console.error(err);
              },
            });
          },
          onClose: () => {
            setLoadingId(null);
          },
        });
      },
      onError(err) {
        console.error(err);
        toast({
          status: "error",
          description: `${subscriptionPlan.name} plan purchase failed`,
        });
        setLoadingId(null);
      },
    });
  };

  const handlePackagePurchase = (addOn: AddOn, inAppPackage: InAppPackage) => {
    setLoadingId(addOn.id);

    buyInAppPackage({
      headers: {
        "X-Account-Hash": XAccountHash,
      },
      body: {
        iapID: inAppPackage._id,
        paymentClient: activePaymentClient,
      },
      onCompleted(params) {
        initializePayment({
          config: {
            email,
            reference: params.data.transactionReference,
            amount: params.data.amount * 100,
          },
          onSuccess: () => {
            setLoadingId(null);
            toast({
              status: "loading",
              description: `${inAppPackage.credits} mails purchase processing`,
            });
            verifyTransaction({
              headers: {
                "X-Account-Hash": XAccountHash || "",
              },
              body: {
                transactionReference: params.data.transactionReference,
                paymentClient: activePaymentClient,
              },
              onCompleted() {
                toast({
                  status: "success",
                  description: `${inAppPackage.credits} mails added`,
                });
                mailCreditsStore.latestCreditsPurchase = inAppPackage.credits;
              },
              onError(err) {
                console.error(err);
              },
            });
          },
          onClose: () => {
            setLoadingId(null);
          },
        });
      },
      onError(err) {
        console.error(err);
        toast({
          status: "error",
          description: `${inAppPackage.credits} mails purchase failed`,
        });
        setLoadingId(null);
      },
    });
  };

  const handleAddOnClick = (addOn: AddOn) => {
    if (addOn.type === "subscription" && addOn.planKind) {
      const subscriptionPlan = subscriptionPlans.find(
        (plan) => plan.name === addOn.planKind
      );
      if (subscriptionPlan) {
        handleSubscriptionPurchase(addOn, subscriptionPlan);
      }
    } else if (addOn.type === "package" && addOn.packageName) {
      const inAppPackage = inAppPackages.find(
        (pkg) => pkg.name.toLowerCase() === addOn.packageName?.toLowerCase()
      );
      if (inAppPackage) {
        handlePackagePurchase(addOn, inAppPackage);
      }
    }
  };

  const getAddOnInfo = (addOn: AddOn) => {
    if (addOn.type === "subscription" && addOn.planKind) {
      const plan = subscriptionPlans.find((p) => p.name === addOn.planKind);
      if (plan) {
        const isActive =
          lwSubscription?.lwsPlanID === plan._id &&
          lwSubscription?.expiredAt &&
          new Date(lwSubscription.expiredAt) > new Date();
        return {
          price: `₦${plan.amount.toLocaleString()}`,
          status: isActive ? "Active" : null,
          buttonText: isActive ? "Active" : "Subscribe",
        };
      }
    } else if (addOn.type === "package" && addOn.packageName) {
      const pkg = inAppPackages.find(
        (p) => p.name.toLowerCase() === addOn.packageName?.toLowerCase()
      );
      if (pkg) {
        return {
          price: `₦${pkg.amount.toLocaleString()}`,
          status: `${pkg.credits} mails`,
          buttonText: "Buy Now",
        };
      }
    }
    return {
      price: "Loading...",
      status: null,
      buttonText: "Loading...",
    };
  };

  return (
    <div className="w-full flex flex-col pt-2 pb-4 mt-2 bg-none">
      <span className="text-[24px] font-[400] optima text-center w-full">
        Add ons
      </span>
      <div className="flex w-full pl-3 pr-3 overflow-x-auto gap-5 no-scrollbar pt-3 py-8 bg-transparent">
        {addOnsConfig.map((addOn) => {
          const Icon = addOn.icon;
          const info = getAddOnInfo(addOn);
          const isLoading = loadingId === addOn.id;

          return (
            <div
              key={addOn.id}
              className="min-w-[150px] w-full overflow-hidden min-h-[90px] border-[#E6439A] glacial bg-[#F3EFEE] shadow-black/30 shadow-lg rounded-[7px] flex flex-col px-3 py-4 gap-3 items-center justify-center"
            >
              <div className="flex flex-col gap-3 items-center">
                <Icon size={35} strokeWidth={1} />
                <span className="uppercase glacial text-[12px]">
                  {addOn.title}
                </span>
              </div>
              <span className="text-[16px] font-[400] glacial">
                {info.status || info.price}
              </span>
              <button
                className="h-[35px] text-[14px] glacial bg-white flex items-center justify-center glacial rounded-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleAddOnClick(addOn)}
                disabled={
                  isLoading ||
                  info.buttonText === "Loading..." ||
                  info.buttonText === "Active"
                }
              >
                {isLoading ? "Processing..." : info.buttonText}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AddOnsSection;
